import { FormConfig, SelectOption } from '@lbcamden/lib-react';
import { deleteUndefined } from '@lbcamden/shared-utils';
import pick from 'lodash/pick';
import { z } from 'zod';

import { personFieldDisplayValues } from '~/client/config/display-values/people';
import { GetPersonFullNameFn } from '~/client/hooks/storeHooks';
import { contactDetailsFormSchema } from '~/common/schemas/people';
import { tempNumericId } from '~/common/tinybase/utils';
import { ReferenceListType } from '~/common/types/data';
import {
  ContactDetailsArrayKey,
  ContactDetailsItem,
  NewPersonPayload,
  Person,
  PersonContactDetails,
  WithoutAddedFields,
  PcfraStatus,
  PersonSearchInput,
  PersonFormValuesResult,
  CreateHouseholdMemberPayload,
  PersonSearchResult,
  RpeepStatus,
} from '~/common/types/people';
import { detailsArrayKeys, getTempContactDetailItem } from '~/common/utils/people';

import { contactDetailsFormConfig } from '../ContactDetailsEditPage/config';
import {
  getInitialFormValues as getInitialContactDetailsFormValues,
  formValuesToUpdatePayload as getContactDetailsUpdatePayload,
} from '../ContactDetailsEditPage/helpers';
import { householdMemberUpdateFormSchema, personFormConfig } from '../formConfig';

export type NewHouseholdMemberFormValues = z.infer<typeof newHouseholdMemberFormSchema>;

export const personSearchFormConfig: FormConfig<PersonSearchInput> = {
  fieldDisplayValues: personFieldDisplayValues,
  fieldList: ['forename', 'surname', 'dateOfBirth'],
  fieldConfigs: {
    dateOfBirth: {
      type: 'date',
    },
  },
};

export const newHouseholdMemberFormSchema = householdMemberUpdateFormSchema
  .omit({ personId: true, propertyId: true, householdId: true, householdPersonId: true })
  .extend(contactDetailsFormSchema.shape);

export const getInitialFormValues = (
  formValues?: Partial<PersonFormValuesResult>,
): Partial<NewHouseholdMemberFormValues> => ({
  ...deleteUndefined(formValues || {}, true),
  ...getInitialContactDetailsFormValues(formValues),
});

export const getNewHouseholdMemberFormConfig = (
  referenceListSelectOptions: (type: ReferenceListType) => SelectOption[],
) => {
  const personConfig = personFormConfig(referenceListSelectOptions, false);

  return {
    fieldDisplayValues: personFieldDisplayValues,
    fieldSections: [
      ...personConfig.fieldSections,
      { title: 'Contact information', fields: detailsArrayKeys },
    ],
    fieldConfigs: {
      ...personConfig.fieldConfigs,
      ...contactDetailsFormConfig.fieldConfigs,
    },
  } as FormConfig<NewHouseholdMemberFormValues>;
};

const getContactDetailList = <TArrayKey extends ContactDetailsArrayKey>(
  arrayKey: TArrayKey,
  personId: number,
  values: string[],
) =>
  values.map((value) =>
    getTempContactDetailItem(arrayKey, personId, value),
  ) as PersonContactDetails[TArrayKey];

export const formValuesToCreatePayload = (
  propertyId: number,
  {
    emailAddresses,
    mobileNumbers,
    smsOnlyNumbers,
    otherPhoneNumbers,
    ...formValues
  }: NewHouseholdMemberFormValues,
  getFullNameFn: GetPersonFullNameFn,
  existingPerson?: PersonSearchResult & PersonFormValuesResult,
  shouldEndPrevHouseholdMember?: boolean,
): CreateHouseholdMemberPayload => {
  const newMember: WithoutAddedFields<Person> = {
    ...formValues,
    propertyId,
    householdId: tempNumericId(),
    householdPersonId: tempNumericId(),
    personId: existingPerson?.personId || tempNumericId(),
    tenancyId: tempNumericId(),
    isTenant: false,
    isMainTenant: false,
    isHistoric: false,
    pcfraStatus: PcfraStatus.None,
    rpeepStatus: RpeepStatus.None,
  };
  const { personId } = newMember;

  const contactDetails: ContactDetailsItem[] = [
    ...getContactDetailList('emailAddresses', personId, emailAddresses),
    ...getContactDetailList('mobileNumbers', personId, mobileNumbers),
    ...getContactDetailList('smsOnlyNumbers', personId, smsOnlyNumbers),
    ...getContactDetailList('otherPhoneNumbers', personId, otherPhoneNumbers),
  ];

  const fullNewMember: NewPersonPayload = {
    ...newMember,
    fullName: getFullNameFn(newMember),
    contactDetails,
  };

  const existingPersonInfo: CreateHouseholdMemberPayload['existingPersonInfo'] = existingPerson && {
    ...pick(existingPerson, ['personId', 'householdPersonId', 'householdId']),
    shouldEndPrevHouseholdMember,
    contactDetailsUpdate: getContactDetailsUpdatePayload(existingPerson, {
      emailAddresses,
      mobileNumbers,
      smsOnlyNumbers,
      otherPhoneNumbers,
    }),
  };

  return { values: fullNewMember, existingPersonInfo };
};
